/**
 * The right rail: "Activity" with a live dot, an entity-kind filter, and the
 * timeline (`ActivityFeed`) for one client or the whole practice. Practice
 * rows are tagged with the client they belong to.
 *
 * With `hideHeader` the rail renders only the feed; a host that draws its own
 * bar (Home's Calendar/Activity tabs) places `ActivityFilterMenu` there and
 * passes the controlled `filters`.
 */
import { Check, ListFilter, PanelRightClose } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { HeaderIconButton } from "@/components/app-header";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { useRealtime } from "@/lib/realtime/RealtimeProvider";
import { cn } from "@/lib/utils";

import type { ForeignNationalActivityLogEvent } from "../../../api/types";
import { ActivityFeed } from "../../clientProfile/tabs/activity/ActivityFeed";
import {
  DEFAULT_ACTIVITY_FILTERS,
  ENTITY_TYPE_OPTIONS,
  filterActivity,
  type ActivityFilters,
  type EntityTypeKey
} from "../../clientProfile/tabs/activity/activityModel";
import {
  useActivityFeed,
  type ActivitySource
} from "../../clientProfile/tabs/activity/useActivityFeed";
import { useUserProfiles } from "../../userProfiles/context/UserProfilesContext";
import { seedClientNames, useClientNames } from "../useClientNames";

type Props = {
  source: ActivitySource;
  onCollapse?: () => void;
  className?: string;
  /** Render only the feed; the host draws the bar and filter menu. */
  hideHeader?: boolean;
  /** Controlled filters (pair with `ActivityFilterMenu`). */
  filters?: ActivityFilters;
  onFiltersChange?: (next: ActivityFilters) => void;
};

const clientPath = (id: string) => `/clients/${encodeURIComponent(id)}`;

/** The entity-kind filter dropdown; highlighted when anything is hidden. */
export const ActivityFilterMenu: FC<{
  filters: ActivityFilters;
  onChange: (next: ActivityFilters) => void;
  className?: string;
}> = ({ filters, onChange, className }) => {
  const allOn = ENTITY_TYPE_OPTIONS.every((o) => filters.entities[o.key]);
  const toggleEntity = (key: EntityTypeKey) =>
    onChange({
      ...filters,
      entities: { ...filters.entities, [key]: !filters.entities[key] }
    });
  const resetEntities = () =>
    onChange({ ...filters, entities: DEFAULT_ACTIVITY_FILTERS.entities });
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "size-7 text-muted-foreground hover:text-foreground",
                !allOn && "text-primary hover:text-primary",
                className
              )}
              aria-label="Filter activity"
              data-testid="activity-filter"
            >
              <ListFilter className="size-4" />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">Filter</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">Show</DropdownMenuLabel>
        {ENTITY_TYPE_OPTIONS.map((o) => (
          <DropdownMenuCheckboxItem
            key={o.key}
            checked={filters.entities[o.key]}
            onCheckedChange={() => toggleEntity(o.key)}
            onSelect={(e) => e.preventDefault()}
            className="text-xs"
          >
            {o.label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={allOn}
          onCheckedChange={resetEntities}
          onSelect={(e) => e.preventDefault()}
          className="text-xs"
          disabled={allOn}
        >
          <Check className="size-3" /> Everything
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={filters.routine}
          onCheckedChange={() =>
            onChange({ ...filters, routine: !filters.routine })
          }
          onSelect={(e) => e.preventDefault()}
          className="text-xs"
          data-testid="activity-filter-routine"
        >
          Routine events
        </DropdownMenuCheckboxItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** The emerald "live" dot: grey while the stream reconnects. */
export const ActivityLiveDot: FC<{ className?: string }> = ({ className }) => {
  const { status } = useRealtime();
  const connected = status === "open";
  return (
    <span
      className={cn(
        "size-1.5 rounded-full",
        connected ? "bg-success" : "bg-muted-foreground/40",
        className
      )}
      title={connected ? "Live" : "Reconnecting"}
      data-testid="activity-live-dot"
      data-connected={connected ? "true" : "false"}
    />
  );
};

export const ActivityRail: FC<Props> = ({
  source,
  onCollapse,
  className,
  hideHeader = false,
  filters: controlledFilters,
  onFiltersChange
}) => {
  const navigate = useNavigate();
  const open = useCallback((href: string) => navigate(href), [navigate]);
  const { get: getProfile } = useUserProfiles();
  const [localFilters, setLocalFilters] = useState<ActivityFilters>(
    DEFAULT_ACTIVITY_FILTERS
  );
  const filters = controlledFilters ?? localFilters;
  const setFilters = onFiltersChange ?? setLocalFilters;
  const feed = useActivityFeed(source, { filters });
  const { nameOf, ensure } = useClientNames();

  const isPractice = source.kind === "practice";

  // Resolve client names for practice rows as they arrive. Paged rows carry
  // the name from the server (seeded below, before they are read); only rows
  // that came over the stream without one need a lookup.
  useEffect(() => {
    if (!isPractice) return;
    ensure(
      feed.events
        .map((ev) => String(ev.foreign_national_id ?? ""))
        .filter(Boolean)
    );
  }, [ensure, feed.events, isPractice]);

  const clientTag = useCallback(
    (ev: ForeignNationalActivityLogEvent) => {
      const id = String(ev.foreign_national_id ?? "").trim();
      if (!id) return null;
      if (ev.foreign_national_name)
        seedClientNames([[id, ev.foreign_national_name]]);
      return { label: nameOf(id) ?? "Client", href: clientPath(id) };
    },
    [nameOf]
  );

  const visible = useMemo(
    () => filterActivity(feed.events, filters, getProfile),
    [feed.events, filters, getProfile]
  );

  return (
    <div
      className={cn("flex h-full min-h-0 flex-col", className)}
      data-testid="activity-rail"
    >
      {hideHeader ? null : (
        <header className="flex h-8 shrink-0 items-center gap-2 border-b border-border bg-fill-1 px-3">
          <ActivityLiveDot />
          <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Activity
          </span>
          <span className="ml-auto flex items-center gap-0.5">
            <ActivityFilterMenu filters={filters} onChange={setFilters} />
            {onCollapse ? (
              <HeaderIconButton
                label="Hide activity"
                onClick={onCollapse}
                data-testid="activity-rail-collapse"
              >
                <PanelRightClose className="size-4" />
              </HeaderIconButton>
            ) : null}
          </span>
        </header>
      )}
      <div className="min-h-0 flex-1 px-2">
        <ActivityFeed
          events={visible}
          getUserProfile={getProfile}
          navigate={open}
          isLoading={feed.isLoading}
          error={feed.error}
          onRetry={feed.reload}
          hasMore={feed.hasMore}
          isLoadingMore={feed.isLoadingMore}
          onLoadMore={feed.loadMore}
          liveIds={feed.liveIds}
          clientTag={isPractice ? clientTag : undefined}
          maxChangeLines={3}
          emptyTitle="No activity yet."
          emptyDescription={
            isPractice
              ? "Changes across your clients, cases and documents will show up here."
              : "Changes to this client, their cases and documents will show up here."
          }
        />
      </div>
    </div>
  );
};
